import styled from "styled-components";
import { DashboardLayout } from "src/components/dashboard";
import { Input } from "src/components/inputs";
import searchIcon from "src/assets/images/input/searchIcon.svg";
import React, { useEffect, useState } from "react";
import { Button, ButtonClass, Flex, Table } from "src/components/ui";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { formatDate } from "src/utils/helpers";
import { Loader } from "src/components/common";
import { toast, ToastContainer } from "react-toastify";
import { theme } from "src/styles/Theme";
import {
  WithdrawalRequestModal,
  ConfirmApprovalModal,
  ConfirmDeclineModal,
} from "src/components/withdrawalRequests";
import {
  useAppDispatch,
  useAppSelector,
  setWithdrawalRequests,
  setLoadingWithdrawalRequests,
  setWithdrawalRequestError,
  setSearchField,
  //   setActiveTab,
  setSelectedType,
  setCurrentPage,
  setSelectedRequest,
  setShowApprovalModal,
  setShowDeclineModal,
  setProcessingRequest,
  updateRequestStatus,
  useLazyGetAllWithdrawalRequestsQuery,
  useProcessWithdrawalRequestMutation,
} from "src/store";
import type {
  WithdrawalApiResponse,
  WithdrawalRequest,
} from "src/store/features/requestSlice";
import { PaginationPropsReff } from "src/components/ui/Pagination";
const WithdrawalRequestsContainer = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  border-radius: 16px;
  padding: 20px;
  margin: 2rem 0;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;

    h1 {
      font-size: 32px;
      font-weight: 600;
      color: ${(props) => props.theme.colors.text_01};
    }

    .right-section {
      display: flex;
      gap: 1rem;
      align-items: center;
    }
  }

  .entries-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid ${(props) => props.theme.colors.gray_03};

    .entries {
      font-size: 14px;
      color: ${(props) => props.theme.colors.text_02};
      font-weight: bold;
    }

    .filter-dropdown {
      select {
        padding: 8px 16px;
        border: 1px solid ${(props) => props.theme.colors.gray_03};
        border-radius: 8px;
        background: white;
        color: ${(props) => props.theme.colors.purple};
        font-weight: 500;
        cursor: pointer;
      }
    }
  }

  .status-tabs {
    display: flex;
    gap: 2rem;
    margin-bottom: 2rem;
    border-bottom: 2px solid ${(props) => props.theme.colors.gray_03};

    button {
      background: none;
      border: none;
      padding: 8px 0;
      color: ${(props) => props.theme.colors.text_02};
      cursor: pointer;
      position: relative;
      font-size: 16px;

      &.active {
        color: ${(props) => props.theme.colors.purple};
        font-weight: 500;

        &:after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background: ${(props) => props.theme.colors.purple};
        }
      }
    }
  }

  .table-container {
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 2rem;
    padding: 1rem;

    button {
      padding: 8px 16px;
      border: 1px solid ${(props) => props.theme.colors.gray_03};
      background: white;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        background: ${(props) => props.theme.colors.gray_02};
      }

      &.active {
        background: ${(props) => props.theme.colors.purple};
        color: white;
        border-color: ${(props) => props.theme.colors.purple};
      }
    }
  }
`;

const WithdrawalRequests = () => {
  const dispatch = useAppDispatch();
  const [exportingRequests, setExportingRequests] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [Ppage, setPage] = useState(1);
  const itemsPerPage = 10;

  // Modal states
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [userDrop, setUserDrop] = useState("customer");

  const {
    withdrawalRequests,
    loadingWithdrawalRequests,
    searchField,
    selectedType,
    currentPage,
    selectedRequest,
    showApprovalModal,
    showDeclineModal,
    processingRequest,
  } = useAppSelector((state) => state.request);

  const [getAllWithdrawalRequests] = useLazyGetAllWithdrawalRequestsQuery();
  const [processWithdrawalRequest] = useProcessWithdrawalRequestMutation();
  const [paginationData, setPaginationData] = useState<PaginationPropsReff>({
    page: 0,
    limit: 0,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    document.title = "Withdrawal Requests";
    fetchWithdrawalRequests();
  }, [dispatch, getAllWithdrawalRequests]);

  // Auto-refresh when component becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && withdrawalRequests.length === 0) {
        fetchWithdrawalRequests();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [withdrawalRequests.length]);

  // Fetch withdrawal requests from API

  useEffect(() => {
    fetchWithdrawalRequests();
  }, [activeTab, userDrop]);

  const fetchWithdrawalRequests = async () => {
    try {
      dispatch(setLoadingWithdrawalRequests(true));
      dispatch(setWithdrawalRequestError(null));

      // Force refetch by passing a timestamp to bypass cache
      const result = await getAllWithdrawalRequests({
        page: 1,
        limit: 10,
        status: activeTab,
        source: userDrop,
      }).unwrap();

      // Handle the actual API response structure
      const data: WithdrawalApiResponse = result;
      // ✅ Extract correctly from your API
      const customerRequests =
        data?.payload?.user?.payload?.data?.withdrawals || [];

      const artisanRequests =
        data?.payload?.artisan?.payload?.data?.withdrawals || [];

      // ✅ Merge both
      const allRequests = [...customerRequests, ...artisanRequests];

      console.log("CUSTOMER:", customerRequests);
      console.log("ARTISAN:", artisanRequests);
      console.log("ALL:", allRequests);
      // Clear existing data first, then set new data
      // dispatch(setWithdrawalRequests([]));
      dispatch(setWithdrawalRequests(allRequests));

      // Force component re-render
      setForceUpdate((prev) => prev + 1);

      console.log("Fetched withdrawal requests:", allRequests.length);
    } catch (error: any) {
      const errorMessage =
        error?.data?.error?.message || "Failed to fetch withdrawal requests";
      dispatch(setWithdrawalRequestError(errorMessage));
      console.error("Error fetching withdrawal requests:", error);
      toast.error(errorMessage);
    } finally {
      dispatch(setLoadingWithdrawalRequests(false));
    }
  };

  // Filter data based on search, status, and user type
  const filteredData = React.useMemo(() => {
    return withdrawalRequests.filter((request) => {
      try {
        // Handle cases where the request structure might be different
        const userName =
          request.user?.first_name && request.user?.last_name
            ? `${request.user.first_name} ${request.user.last_name}`.toLowerCase()
            : "";
        const userEmail = request.user?.email?.toLowerCase() || "";

        const matchesSearch =
          userName.includes(searchField.toLowerCase()) ||
          userEmail.includes(searchField.toLowerCase()) ||
          request.amount?.toString().includes(searchField);

        const matchesStatus =
          activeTab === "" ||
          (activeTab === "pending" && request.request_status === "pending") ||
          (activeTab === "approved" && request.request_status === "approved") ||
          (activeTab === "rejected" && request.request_status === "rejected");

        const matchesUserType =
          (userDrop === "customer" && request.user_type === "customer") ||
          (userDrop === "vendor" && request.user_type === "artisan");

        return matchesSearch && matchesStatus && matchesUserType;
      } catch (error) {
        console.error("Error filtering request:", error, request);
        return false;
      }
    });
  }, [withdrawalRequests, selectedType, activeTab, searchField]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handleExport = () => {
    setExportingRequests(true);
    // Simulate export delay
    setTimeout(() => {
      toast.success("Export completed!");
      setExportingRequests(false);
    }, 2000);
  };

  const handleChange = (e: any) => {
    dispatch(setSearchField(e.target.value));
    dispatch(setCurrentPage(1)); // Reset to first page when searching
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    dispatch(setCurrentPage(1)); // Reset to first page when changing tabs
  };

  const handleTypeChange = (type: string) => {
    dispatch(setSelectedType(type));
    setUserDrop(type);
    dispatch(setCurrentPage(1)); // Reset to first page when changing type
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return theme.colors.purple;
      case "pending":
        return "#55C4F1";
      case "rejected":
        return "#EB5656";
      default:
        return theme.colors.text_02;
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "approved":
        return "Approved";
      case "pending":
        return "Pending";
      case "declined":
        return "Declined";
      default:
        return status;
    }
  };

  // Modal handlers
  const handleViewDetails = (request: any) => {
    dispatch(setSelectedRequest(request));
    setIsDetailsModalOpen(true);
  };

  const navigate = useNavigate();

  const handleViewProfile = (request: WithdrawalRequest) => {
    navigate(
      `${
        request.user_type === "customer"
          ? `/customers/${request.user._id}`
          : `/vendors/${request.user._id}`
      }`,
    );
  };

  const handleApprove = (requestId: string) => {
    setIsDetailsModalOpen(false);
    dispatch(setShowApprovalModal(true));
  };

  const handleDecline = (requestId: string) => {
    setIsDetailsModalOpen(false);
    dispatch(setShowDeclineModal(true));
  };

  const handleConfirmApprove = async () => {
    if (selectedRequest) {
      try {
        dispatch(setProcessingRequest(true));

        await processWithdrawalRequest({
          userType: selectedRequest.user_type,
          status: "approved",
          withdrawalId: selectedRequest._id,
        }).unwrap();

        // Update the request status in Redux
        dispatch(
          updateRequestStatus({
            id: selectedRequest._id,
            status: "approved",
          }),
        );

        toast.success("Request approved successfully!");

        // Optionally refresh the data to ensure consistency
        // await fetchWithdrawalRequests();
      } catch (error: any) {
        const errorMessage =
          error?.data?.error?.message ||
          "Failed to approve request. Please try again.";
        console.error("Error approving request:", error);
        toast.error(errorMessage);
      } finally {
        dispatch(setProcessingRequest(false));
      }
    }
    dispatch(setShowApprovalModal(false));
    dispatch(setSelectedRequest(null));
  };

  const handleConfirmDecline = async () => {
    if (selectedRequest) {
      try {
        dispatch(setProcessingRequest(true));

        await processWithdrawalRequest({
          userType: selectedRequest.user_type,
          status: "rejected",
          withdrawalId: selectedRequest._id,
        }).unwrap();

        // Update the request status in Redux
        dispatch(
          updateRequestStatus({
            id: selectedRequest._id,
            status: "declined",
          }),
        );

        toast.success("Request declined successfully!");

        // Optionally refresh the data to ensure consistency
        // await fetchWithdrawalRequests();
      } catch (error: any) {
        const errorMessage =
          error?.data?.error?.message ||
          "Failed to decline request. Please try again.";
        console.error("Error declining request:", error);
        toast.error(errorMessage);
      } finally {
        dispatch(setProcessingRequest(false));
      }
    }
    dispatch(setShowDeclineModal(false));
    dispatch(setSelectedRequest(null));
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    dispatch(setSelectedRequest(null));
  };

  const WithdrawalRequestsTableHeaders = [
    {
      title: "User",
      render: (row: any) => (
        <Flex gap="10px" align="center">
          {/* <img
						style={{ width: 40, height: 40, borderRadius: "50%" }}
						src={`https://via.placeholder.com/40?text=${row.user?.first_name?.charAt(
							0
						)}`}
						alt=''
					/> */}
          <Link
            to={`${
              row.user_type === "customer"
                ? `/customers/${row.user?._id}`
                : `/vendors/${row.user?._id}`
            }`}
          >
            <p style={{ fontWeight: 500 }} className="truncate">{`${
              row.user?.first_name || ""
            } ${row.user?.last_name || ""}`}</p>
            <p style={{ fontSize: "0.9em", color: "#666" }}>
              {row.user?.phone || ""}
            </p>
          </Link>
        </Flex>
      ),
    },
    {
      title: "Email",
      render: (row: any) => row.user?.email || "",
    },
    {
      title: "Date",
      render: (row: any) => (
        <span className="truncate">
          {row.createdAt ? formatDate(row.createdAt) : ""}
        </span>
      ),
    },
    {
      title: "Amount",
      render: (row: any) =>
        `${row.user?.currency_symbol || ""}${
          row.amount ? row.amount.toLocaleString() : "0"
        }`,
    },
    {
      title: "Type",
      render: (row: any) =>
        row.user_type
          ? row.user_type.charAt(0).toUpperCase() + row.user_type.slice(1)
          : "",
    },
    {
      title: "Status",
      render: (row: any) => (
        <span
          style={{ color: getStatusColor(row.request_status), fontWeight: 500 }}
        >
          {getStatusDisplay(row.request_status)}
        </span>
      ),
    },
    {
      title: "",
      render: (row: any) => (
        <button
          onClick={() => handleViewDetails(row)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="View Details"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ color: theme.colors.purple }}
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </button>
      ),
    },
  ];

  return (
    <DashboardLayout
      pageTitle="Withdrawal Requests"
      rhsHeading={
        <RhsHeading
          handleChange={handleChange}
          onClick={handleExport}
          exportingRequests={exportingRequests}
          loading={loadingWithdrawalRequests}
        />
      }
    >
      <ToastContainer />
      <WithdrawalRequestsContainer
        key={`withdrawal-requests-${withdrawalRequests.length}-${selectedType}-${activeTab}-${forceUpdate}`}
      >
        <div className="flex items-center gap-4">
          <span className="text-primary">
            {filteredData.length} entries showing for
          </span>

          <select
            value={selectedType}
            className="bg-primary/20 rounded-full px-4 p-2"
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="customer">Customer</option>
            <option value="vendor">Vendor</option>
          </select>
        </div>

        <div className="status-tabs">
          <button
            className={activeTab === "" ? "active" : ""}
            onClick={() => handleTabChange("")}
          >
            All
          </button>
          <button
            className={activeTab === "approved" ? "active" : ""}
            onClick={() => handleTabChange("approved")}
          >
            Approved
          </button>
          <button
            className={activeTab === "pending" ? "active" : ""}
            onClick={() => handleTabChange("pending")}
          >
            Pending
          </button>
          <button
            className={activeTab === "rejected" ? "active" : ""}
            onClick={() => handleTabChange("rejected")}
          >
            Declined
          </button>
        </div>

        {loadingWithdrawalRequests ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "100px",
            }}
          >
            <Loader>Loading...</Loader>
          </div>
        ) : paginatedData.length > 0 ? (
          <>
            <div className="table-container">
              <Table
                key={`table-${paginatedData.length}-${selectedType}-${activeTab}-${forceUpdate}`}
                rows={paginatedData}
                headers={WithdrawalRequestsTableHeaders}
                showHead={true}
              />
            </div>

            <div className="pagination">
              <button
                onClick={() =>
                  dispatch(setCurrentPage(Math.max(1, currentPage - 1)))
                }
                disabled={currentPage === 1}
              >
                ← Previous
              </button>

              {Array.from({ length: Math.min(6, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    className={currentPage === pageNum ? "active" : ""}
                    onClick={() => dispatch(setCurrentPage(pageNum))}
                  >
                    {pageNum}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  dispatch(
                    setCurrentPage(Math.min(totalPages, currentPage + 1)),
                  )
                }
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "100px",
              fontSize: "18px",
              color: "#666",
            }}
          >
            No Withdrawal Requests Found
          </div>
        )}
      </WithdrawalRequestsContainer>

      {/* Modals */}
      <WithdrawalRequestModal
        isOpen={isDetailsModalOpen}
        onClose={closeDetailsModal}
        request={selectedRequest}
        onApprove={handleApprove}
        onDecline={handleDecline}
      />

      <ConfirmApprovalModal
        isOpen={showApprovalModal}
        onClose={() => dispatch(setShowApprovalModal(false))}
        onConfirm={handleConfirmApprove}
        isLoading={processingRequest}
      />

      <ConfirmDeclineModal
        isOpen={showDeclineModal}
        onClose={() => dispatch(setShowDeclineModal(false))}
        onConfirm={handleConfirmDecline}
        isLoading={processingRequest}
      />
    </DashboardLayout>
  );
};

interface Props {
  handleChange: (e: any) => void;
  onClick: () => void;
  exportingRequests: boolean;
  loading: boolean;
}

export const RhsHeading: React.FC<Props> = ({
  handleChange,
  onClick,
  exportingRequests,
  loading,
}) => (
  <Flex wrap="wrap" gap="10px">
    <Button
      onClick={onClick}
      classes={[ButtonClass.SOLID, ButtonClass.WITH_ICON]}
      style={{
        backgroundColor: theme.colors.purple,
        height: "48px",
        minWidth: "145px",
      }}
    >
      {exportingRequests ? <Loader>Exporting...</Loader> : <span>Export</span>}
    </Button>
    <Input
      icon={<img src={searchIcon} alt="searchIcon" />}
      type="search"
      placeholder="Search"
      handleChange={handleChange}
    />
  </Flex>
);

export default WithdrawalRequests;
